import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { subscribeToEvidenceInvalidation } from '@/lib/knowledge/evidence-cache-invalidation';

import { useAuth } from './auth-context';
import { CacheScopeContext, type CacheScope } from './cache-scope-context';
import { useOrganization } from './organization-context';

/**
 * Publishes the current viewer and organisation to `useCacheScope()`. Mounted
 * inside both the auth and the organisation providers; re-renders its value
 * when identity, selected office or the viewer's observed access changes.
 */
export function CacheScopeProvider({ children }: Readonly<{ children: ReactNode }>) {
  const client = useQueryClient();
  const { user } = useAuth();
  const { currentOrganization, userOrganizations } = useOrganization();
  const actorId = user?.id ?? null;
  const [accessEpoch, setAccessEpoch] = useState(0);
  useEffect(
    () =>
      subscribeToEvidenceInvalidation(client, actorId, () => setAccessEpoch((epoch) => epoch + 1)),
    [client, actorId],
  );
  const organizationId = currentOrganization?.id ?? null;
  const ownMembership = (userOrganizations ?? []).find(
    (membership) => membership.organization_id === organizationId,
  );
  const accessRevision = ownMembership ? `${ownMembership.role}|${ownMembership.status}` : null;
  const value = useMemo<CacheScope>(
    () => ({ actorId, organizationId, accessRevision, accessEpoch }),
    [actorId, organizationId, accessRevision, accessEpoch],
  );
  return <CacheScopeContext.Provider value={value}>{children}</CacheScopeContext.Provider>;
}
